import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Dedicated landing for the 0–1 v segment. Targets:
//   "montessori vauva", "montessori lelut vauvalle", "vauvan lelut 0-6kk",
//   "kiipeilykaari vauvalle", "lattiapatja vauvalle".
// These are lower-competition long-tails where new parents actively search,
// and no existing page owns them cleanly (kauppa is too generic, koulu is
// editorial).
const SLUG = "montessori-vauva";
const H1 = "Montessori-vauva 0–12 kk — kiipeilykaari, lattiapatja & pehmoiset palikat";
const TITLE = "Montessori vauvalle 0–12 kk — kaari, patja, palikat | Montessori24";
const DESC =
  "Montessori-lelut ja -kalusteet vauvalle 0–12 kk: pehmoiset IGLU-palikat, kiipeilykaari, lattiapatja ja Pikler-alusta. Koivuvaneri, ilmainen toimitus Suomeen 3–7 pv.";

const FAQS = [
  {
    q: "Milloin vauva voi aloittaa Montessori-lelut?",
    a: "Heti syntymästä. Ensimmäiset kuukaudet vauva makaa lattialla ja seuraa katseellaan — silloin sopivat mustavalkoiset mobilet ja pehmoiset tekstiilipalikat. 3–6 kk vauva alkaa tarttua ja työntää — kiipeilykaari toimii kurkotusalustana. 6–12 kk vauva nousee itsenäisesti pystyyn: Pikler-kaari, matala hylly ja pehmopalikat tukevat karkeamotoriikkaa.",
  },
  {
    q: "Mitä eroa on Montessori-lattiapatjalla ja pinnasängyllä?",
    a: "Montessori-lattiapatja on suoraan lattialla oleva patja (esim. 60×120 cm), jolta vauva pääsee itse alas ja takaisin heti kun oppii ryömimään. Pinnasänky rajoittaa: aikuinen nostaa vauvan sisään ja ulos. Lattiapatja tukee itsenäistä nukahtamista ja liikkumista — Montessori- ja Pikler-pedagogiikan ydin.",
  },
  {
    q: "Sopiiko Pikler-kaari alle 1-vuotiaalle?",
    a: "Kaari on Pikler-välineistä matalin: vauva makaa kaaren alla ja tarttuu puolista, ja myöhemmin vetää itsensä ylös sen avulla. Kolmio tulee ajankohtaiseksi vasta kun lapsi seisoo tukevasti. Ikäsuositus vaihtelee mallin ja valmistajan mukaan — tarkista aina tuotekohtainen ikäsuositus ennen käyttöä.",
  },
  {
    q: "Ovatko IGLU-palikat turvallisia vauvalle?",
    a: "Ovat. IGLU-palikoiden pehmeä huopa- tai kangaspinta ei satuta jos vauva kaatuu niiden päälle, mutta samalla ne ovat tukevia (vaahtomuovitäyte). Tarkista tuotekohtainen ikäsuositus ja turvallisuustiedot tuotesivulta. Tarkista tuotekohtainen ikäsuositus tuotesivulta.",
  },
  {
    q: "Miten järjestän vauvan huoneen Montessori-periaatteilla?",
    a: "Neljä kulmakiveä: 1) Matala lattiapatja pinnasängyn sijaan. 2) Matala hylly, jolla 5–6 valittua esinettä lapsen tasolla — ei täyttä leluteltaa. 3) Peilinurkka lattian tasolla — vauva tarkkailee itseään. 4) Liikuntanurkka jossa kiipeilykaari + pehmomatto. Ei kirkasvärisiä kirsikkakoristeita — luonnonvaaleat sävyt tukevat keskittymistä.",
  },
  {
    q: "Mikä on hyvä ensilahja Montessori-vauvalle?",
    a: "Pehmeät IGLU-palikat (0–3 v käyttö), kiipeilykaari (3 kk–3 v) tai puinen tarttumarengas ovat kestäviä ensilahjoja. Kaikki kolme kestävät useita vuosia käyttöä — sama esine palvelee vauva-, taapero- ja leikki-ikäisenä eri tavalla. Ekologinen ja pitkäikäinen valinta lastenhuoneeseen.",
  },
];

export const Route = createFileRoute("/montessori-vauva")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: MontessoriVauvaPage,
});

function MontessoriVauvaPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Montessori-vauva 0–12 kk"
      title={H1}
      intro="Vauvan ensimmäinen vuosi on herkintä oppimisaikaa. Montessori- ja Pikler-välineet vauvalle: pehmoiset IGLU-palikat, kiipeilykaari, lattiapatja ja tarttumaesineet. Tuotekohtaiset ikäsuositukset, materiaalit ja turvallisuustiedot löydät jokaiselta tuotesivulta."
      kauppaCategory="age-0-1"
      match={(t, v) =>
        /iglu/i.test(v ?? "") ||
        /kaari|arch|pehmo|pehmeä|pallomeri|matto|matt|kiila|wobble|rainbow|sateenkaari|sensor|mega ?wave|megalaine|patja|mattress|floor bed|mobile|helmi|bead|tarttum/i.test(t)
      }
      relatedChips={[
        { label: "IGLU-palikat", to: "/iglu" },
        { label: "Pikler-kolmiot", to: "/pikler-kolmio" },
        { label: "Lattiapatja-opas", to: "/montessori-koulu/montessori-lattiapatja" },
        { label: "Montessori-lelut", to: "/montessori-lelut" },
        { label: "Kalusteet kotiin -opas", to: "/montessori-koulu/montessori-kalusteet-kotiin" },
      ]}
      body={[
        {
          heading: "Vauvan ensimmäinen vuosi — mikä väline mihinkin ikään",
          paragraphs: [
            "0–3 kk: vauva makaa selällään ja seuraa katseellaan. Mustavalkoiset kontrastikuvat, kevyt mobile ja pehmeä alusta riittävät. Kiipeilykaari toimii jo tässä vaiheessa — vauva makaa sen alla, kurkottaa käsillään ja aivot yhdistävät näön liikkeeseen.",
            "3–6 kk: vauva alkaa kääntyä, tarttua ja työntyä käsivarsilla. Pehmoiset IGLU-palikat, puiset tarttumaesineet ja peili lattiatasolla stimuloivat aisteja ilman että vauvan tarvitsee istua tuetusti.",
            "6–9 kk: vauva istuu itsenäisesti ja alkaa ryömiä. Matala hylly, jolla 5–6 valittua esinettä, opettaa lasta valitsemaan ja palauttamaan. IGLU-palikat toimivat rakentamisen ensiaskeleina.",
            "9–12 kk: vauva vetää itsensä ylös ja ottaa ensiaskeleet. Pikler-kaari on nyt turvallinen tukikaide, ja lattiapatjalta pääsee ryömien nukkumaan ja takaisin. Matala tuoli ja lastenpöytä valmistavat itsenäiseen syömiseen.",
          ],
        },
        {
          heading: "Miksi lattiapatja pinnasängyn sijaan?",
          paragraphs: [
            "Perinteinen pinnasänky on häkki: aikuinen päättää, koska vauva on siellä ja koska ei. Montessori- ja Pikler-tutkimus painottaa vapaata liikettä — vauva harjoittelee kääntymistä, ryömimistä ja pystyyn nousua paljon nopeammin kun ei tarvitse odottaa aikuista.",
            "Käytännössä: 60×120 cm patja suoraan lattialle, ympärille pehmeä matto ja huoneesta poistetaan pienet vaaralliset esineet. Vauva nukkuu turvallisesti, herää itse ja voi ryömiä leikkinurkkaan ilman että aikuisen tarvitsee kiirehtiä paikalle. Yönukkumisen laatu ei tutkimuksissa eroa pinnasängystä — mutta päiväaktiivisuus on selvästi virikkeisempää.",
          ],
        },
        {
          heading: "Vauvan huoneen neljä nurkkaa Montessori-periaatteilla",
          paragraphs: [
            "Nukkumanurkka: matala patja lattialla, kevyt peitto, ei koristelevaa palautetta. Rauhoittavat sävyt (kerma, luonnonvalkoinen, tumma puu).",
            "Liikuntanurkka: kiipeilykaari + iso pehmomatto. 3 kk vauvalle kaari on kurkotustaso, 10 kk vauvalle tukikaide pystyyn nousuun.",
            "Peilinurkka: 40×60 cm turvapeili lattian tasolla. Vauva tarkkailee omia liikkeitään — perusta minäkäsityksen ja motoriikan yhteistyölle.",
            "Leluhylly: matala 2-hyllyinen kaappi, jolla 5–6 esinettä kerrallaan. Vaihda esineitä 1–2 viikon välein. Vähemmän on enemmän — vauva keskittyy syvemmin kun valintoja on rajallisesti.",
          ],
        },
      ]}
      faqs={FAQS}
      cta={{
        heading: "Aloita Montessori-arki vauvasta lähtien",
        body: "IGLU-palikat, kiipeilykaari ja lattiapatja — turvatestatut vauvan ensimmäiseen vuoteen. Ilmainen toimitus 3–7 päivässä.",
        primaryLabel: "Selaa 0–1 v mallistoa",
        secondaryLabel: "Lue lattiapatja-opas",
        secondaryTo: "/montessori-koulu/montessori-lattiapatja",
      }}
    />
  );
}